import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Outlet } from "react-router-dom";
import Banner from "./Banner";
import { NavBar } from "./NavBar/NavBar";
import SideNav from "./SideNav/SideNav";
import Footer from "./Footer";
import useData from "../Hooks/useData";
import { useUser } from "../Hooks/useUser";
import { Box, Button, CircularProgress, Dialog, DialogActions, DialogContent, DialogTitle, Typography } from "@mui/material";
import DOMPurify from "dompurify";
import useAxiosPrivate from "../Hooks/useAxiosPrivate";
import useLogout from "../Hooks/useLogout";

/** Matches admin UserEditForm: production must resolve relative media paths against the API host. */
const getApiBase = () => {
  const fromEnv = process.env.REACT_APP_API_URL &&
    String(process.env.REACT_APP_API_URL).replace(/\/+$/, '');
  if (fromEnv) return fromEnv;
  if (window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1') {
    return 'http://localhost:3001';
  }
  return 'https://baptistmenapi.onrender.com';
};

function resolveProfileImageUrl(rawPath) {
  if (!rawPath) return null;
  const s = String(rawPath).trim();
  if (!s) return null;
  const base = /^https?:\/\//i.test(s) ? s : `${getApiBase()}${s.startsWith('/') ? '' : '/'}${s}`;
  const sep = base.includes('?') ? '&' : '?';
  return `${base}${sep}t=${Date.now()}`;
}

const Layout = () => {
  const user = useUser();
  const { auth } = useData();
  const axiosPrivate = useAxiosPrivate();
  const logoutHook = useLogout();

  const isLoggedIn = Boolean(auth);

  const [profilePicture, setProfilePicture] = useState(null);
  const [bgReminder, setBgReminder] = useState({
    enabled: false,
    missingDate: false,
  });

  useEffect(() => {
    const uid = user?.user?.UserID;
    if (!uid) {
      setProfilePicture(null);
      setBgReminder({ enabled: false, missingDate: false });
      return;
    }
    let cancelled = false;
    (async () => {
      try {
        const [userResp, bgSettingsResp] = await Promise.all([
          axiosPrivate.get(`/api/users/${uid}`),
          axiosPrivate.get('/api/background-check/settings').catch(() => ({ data: { enabled: false } })),
        ]);
        if (cancelled) return;

        const ud = userResp.data || {};
        const pathCandidate =
          ud.ProfileImage ||
          ud.userSettings?.UserPicture ||
          (typeof ud.MiddleName === 'string' && ud.MiddleName.startsWith('/uploads/')
            ? ud.MiddleName
            : '');

        setProfilePicture(pathCandidate ? resolveProfileImageUrl(pathCandidate) : null);

        const bgEnabled = Boolean(bgSettingsResp?.data?.enabled);
        const rawBgDate = ud.userSettings?.BP_BGCheckDate;
        const hasBgDate = Boolean(rawBgDate && String(rawBgDate).trim() !== '');

        let isBgExpired = false;
        if (hasBgDate) {
          const d = new Date(rawBgDate);
          if (!isNaN(d.getTime())) {
            const expiry = new Date(d.getFullYear() + 3, 11, 31);
            isBgExpired = new Date() > expiry;
          }
        }

        setBgReminder({
          enabled: bgEnabled,
          missingDate: bgEnabled && (!hasBgDate || isBgExpired),
          isExpired: isBgExpired,
        });
      } catch (e) {
         if (cancelled || e?.response?.status === 401 || e?.response?.status === 403) {
          // token already invalid — don't hammer more endpoints, let logout() handle it
          return;
          }
        if (!cancelled) {
          try {
            const userSettingsResp = await axiosPrivate.get(`/api/users/usersettings/${uid}`);
            const pathLegacy = userSettingsResp.data?.UserPicture;
            setProfilePicture(pathLegacy ? resolveProfileImageUrl(pathLegacy) : null);

            const rawBgDate = userSettingsResp?.data?.BP_BGCheckDate;
            const bgSettingsResp = await axiosPrivate
              .get('/api/background-check/settings')
              .catch(() => ({ data: { enabled: false } }));
            const bgEnabled = Boolean(bgSettingsResp?.data?.enabled);
            const hasBgDate = Boolean(rawBgDate && String(rawBgDate).trim() !== '');
            let isBgExpired = false;
            if (hasBgDate) {
              const d = new Date(rawBgDate);
              if (!isNaN(d.getTime())) {
                const expiry = new Date(d.getFullYear() + 3, 11, 31);
                isBgExpired = new Date() > expiry;
              }
            }
            setBgReminder({
              enabled: bgEnabled,
              missingDate: bgEnabled && (!hasBgDate || isBgExpired),
              isExpired: isBgExpired,
            });
          } catch {
            setProfilePicture(null);
            setBgReminder({ enabled: false, missingDate: false });
          }
        }
      }
    })();
    return () => { cancelled = true; };
  }, [user?.user?.UserID, axiosPrivate]);

  const [termsState, setTermsState] = useState({
    checking: false,
    required: false,
    heading: "",
    content: "",
    lastUpdated: null,
    acceptedAt: null,
  });
  const [hasCheckedTerms, setHasCheckedTerms] = useState(false);
  const [isAccepting, setIsAccepting] = useState(false);

  const sanitizedHtml = useMemo(() => {
    return DOMPurify.sanitize(termsState.content || "");
  }, [termsState.content]);

  const formattedTermsLastUpdated = useMemo(() => {
    if (!termsState.lastUpdated) return "";
    const d = new Date(termsState.lastUpdated);
    if (Number.isNaN(d.getTime())) return "";
    return new Intl.DateTimeFormat("en-US", {
      month: "long",
      day: "2-digit",
      year: "numeric",
    }).format(d);
  }, [termsState.lastUpdated]);

  const fetchTermsStatus = useCallback(async () => {
    // While we determine if the user can proceed, block interaction.
    setTermsState((prev) => ({ ...prev, checking: true, required: false }));
    try {
      const resp = await axiosPrivate.get("/api/terms/status");
      const data = resp?.data || {};
      setTermsState({
        checking: false,
        required: Boolean(data.required),
        heading: data.heading ?? "",
        content: data.content ?? "",
        lastUpdated: data.lastUpdated ?? null,
        acceptedAt: data.acceptedAt ?? null,
      });
    } catch (e) {
      // Fail closed: if we can't determine status, require accept (blocks the app).
      setTermsState((prev) => ({
        ...prev,
        checking: false,
        required: true,
        heading: prev.heading || "Terms & Conditions",
        content: prev.content || "<p>Unable to load Terms &amp; Conditions. Please try again or contact support.</p>",
      }));
    } finally {
      setHasCheckedTerms(true);
    }
  }, [axiosPrivate, user?.user?.UserID]);

  useEffect(() => {
    if (!isLoggedIn) {
      setTermsState({
        checking: false,
        required: false,
        heading: "",
        content: "",
        lastUpdated: null,
        acceptedAt: null,
      });
      setHasCheckedTerms(false);
      return;
    }
    setHasCheckedTerms(false);
    fetchTermsStatus();
  }, [isLoggedIn, fetchTermsStatus]);

  const handleAcceptTerms = async () => {
    setIsAccepting(true);
    try {
      await axiosPrivate.post("/api/terms/accept", {});
      await fetchTermsStatus();
    } finally {
      setIsAccepting(false);
    }
  };

  const handleCancelTerms = async () => {
    await logoutHook();
};

  const gateOpen = isLoggedIn && hasCheckedTerms && termsState.required;
  //console.log('Auth: ', auth, user);
  return (
    <div>
      <Dialog
        open={gateOpen}
        disableEscapeKeyDown
        fullWidth
        maxWidth="md"
        sx={{ zIndex: 10000 }}
        BackdropProps={{
          sx: {
            backgroundColor: "rgba(0,0,0,0.7)",
          },
        }}
        PaperProps={{
          sx: {
            borderRadius: 2,
          },
        }}
        onClose={(event, reason) => {
          // Do not allow closing via backdrop or escape.
          if (reason === "backdropClick" || reason === "escapeKeyDown") return;
        }}
      >
        <DialogTitle>
          <Box sx={{ display: "flex", justifyContent: "space-between", gap: 3 }}>
            <Box sx={{ minWidth: 0 }}>
              <Typography
                variant="h5"
                sx={{
                  fontWeight: 800,
                  letterSpacing: 0.5,
                  textTransform: "uppercase",
                }}
              >
                TERMS AND CONDITIONS
              </Typography>
              <Typography variant="body2" sx={{ mt: 0.75, color: "text.secondary", textTransform: "none" }}>
                Please review our Terms &amp; Conditions below. You must scroll through the entire agreement before you can accept.
              </Typography>
            </Box>

            <Box sx={{ flexShrink: 0, pt: 0.5 }}>
              <Typography
                variant="body2"
                sx={{
                  fontWeight: 700,
                  whiteSpace: "nowrap",
                }}
              >
                UPDATED:{" "}
                <Box component="span" sx={{ fontWeight: 400 }}>
                  {formattedTermsLastUpdated || "—"}
                </Box>
              </Typography>
            </Box>
          </Box>
        </DialogTitle>
        <DialogContent
          dividers
          sx={{
            maxHeight: "70vh",
            overflowY: "auto",
          }}
        >
          {termsState.checking ? (
            <Box sx={{ display: "flex", alignItems: "center", gap: 2, py: 2 }}>
              <CircularProgress size={22} />
              <Typography>Please wait…</Typography>
            </Box>
          ) : (
            <Box>
              <Box
                sx={{ typography: "body1" }}
                dangerouslySetInnerHTML={{ __html: sanitizedHtml }}
              />
            </Box>
          )}
        </DialogContent>
        <DialogActions sx={{ p: 2, gap: 1 }}>
          <Button
            variant="outlined"
            color="error"
            onClick={handleCancelTerms}
            disabled={termsState.checking || isAccepting}
          >
            Cancel
          </Button>
          <Button
            variant="contained"
            onClick={handleAcceptTerms}
            disabled={termsState.checking || isAccepting}
          >
            {isAccepting ? "Accepting..." : "Accept"}
          </Button>
        </DialogActions>
      </Dialog>

      <NavBar user={user} profilePicture={profilePicture} />
      <div id="layoutSidenav">
        <div id="layoutSidenav_nav">
          <SideNav user={user} profilePicture={profilePicture} backgroundCheckReminder={bgReminder} />
        </div>

        <div id="layoutSidenav_content">
          {auth && <Banner />}
          <Outlet context={{ backgroundCheckReminder: bgReminder }} />
          <Footer />
        </div>
      </div>
    </div>
  );
};

export default Layout;
